/** Toast-uri (feedback scurt). Culoare pe tip: info/ok/warn/err. */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type ToastKind = 'info' | 'ok' | 'warn' | 'err';
interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  body?: string;
}

interface ToastCtx {
  push: (t: { kind?: ToastKind; title: string; body?: string }) => void;
}

const Ctx = createContext<ToastCtx | null>(null);
let counter = 0;

const ACCENTS: Record<ToastKind, string> = {
  info: 'var(--accent)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  err: 'var(--err)',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const push = useCallback<ToastCtx['push']>(
    (t) => {
      const id = ++counter;
      const toast: Toast = { id, kind: t.kind ?? 'info', title: t.title, body: t.body };
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => remove(id), 5000);
    },
    [remove],
  );

  const value = useMemo(() => ({ push }), [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="rv-toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="rv-toast" style={{ borderLeftColor: ACCENTS[t.kind] }}>
            <button
              onClick={() => remove(t.id)}
              aria-label="Închide"
              style={{ float: 'right', color: 'var(--muted)', fontWeight: 800, marginLeft: 8 }}
            >
              ✕
            </button>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{t.title}</div>
            {t.body && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>{t.body}</div>}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast trebuie folosit în ToastProvider');
  return ctx;
}
